/**
 * Visual Content Loop utility functions
 * Centralized utilities for VCL-specific operations
 */

/**
 * Creates a backfill indicator object for display in VCL UI
 * @param {string} providerId - The provider ID to associate with this backfill indicator
 * @param {number} backfillSlots - Number of backfill slots needed
 * @returns {Object} Backfill indicator object
 */
export const createBackfillIndicator = (providerId, backfillSlots) => {
	return {
		id:                  `backfill-${providerId}`,
		name:                `BACKFILLED × ${backfillSlots}`,
		duration:            0,
		slots:               backfillSlots,
		order:               999, // Always show at bottom
		isBackfillIndicator: true,
	};
};

export const VCL_RESOLUTION_HORIZONTAL = '1920x1080';
export const VCL_RESOLUTION_VERTICAL = '1080x1920';

/**
 * Flips a VCL resolution between vertical and horizontal
 * @param {string} resolution - Current resolution (WIDTHxHEIGHT)
 * @returns {string} The other resolution
 */
export const toggleVCLResolution = (resolution) => {
	return resolution === VCL_RESOLUTION_VERTICAL ? VCL_RESOLUTION_HORIZONTAL : VCL_RESOLUTION_VERTICAL;
};

/**
 * Resolves the effective VCL resolution: explicit VCL value > account default > vertical
 * @param {string|null} explicitResolution - The VCL's own resolution column (null = inherit)
 * @param {string} accountVCLProvidersJSON - account.visual_content_loop_providers JSON string
 * @returns {string} Effective resolution
 */
export const getEffectiveVCLResolution = (explicitResolution, accountVCLProvidersJSON) => {
	if (explicitResolution) {
		return explicitResolution;
	}

	try {
		const settings = JSON.parse(accountVCLProvidersJSON || '{}')?.settings || {};
		if (settings.resolution) {
			return settings.resolution;
		}
	} catch (err) {
		// malformed account JSON falls through to the default
	}

	return VCL_RESOLUTION_VERTICAL;
};

/**
 * @param {string} resolution - Resolution (WIDTHxHEIGHT)
 * @returns {boolean} True unless the resolution is the horizontal one
 */
export const isVCLResolutionVertical = (resolution) => {
	return resolution !== VCL_RESOLUTION_HORIZONTAL;
};

/**
 * Resolves the pending VCL id for a brood's visual_content_loop_id setting
 * from Crow's pendingUpdates map (DEV-11279).
 *
 * The pendingUpdates key shape differs by system topology:
 * - multi-brood systems store it flat:   pendingUpdates['<entityKey>.<settingKey>.int'] = 5
 * - single-brood systems store it nested: pendingUpdates['<entityKey>.<settingKey>'] = { int: 5 }
 *
 * @param {Object|undefined} pendingUpdates - Crow pendingUpdates map
 * @param {string} m1Key - '<entityKey>.<settingKey>' for the visual_content_loop_id setting
 * @returns {number|undefined} pending VCL id (0 = pending Unassigned), or undefined when none is pending
 */
export const resolvePendingVCLID = (pendingUpdates, m1Key) => {
	const updates = pendingUpdates || {};
	return updates[`${m1Key}.int`] ?? updates[m1Key]?.int ?? undefined;
};

export const getPersistableData = (providers) => {
	return (providers || []).map((p) => {
		return {
			id: p.id,
			type: p.type,
			category: p.category ?? null,
			slots: p.slots ?? 1,
			order: p.order ?? 1,
			isDeleted: Boolean(p.isDeleted),
			settings: p.settings || {},
		};
	});
};

export const stableStringify = (obj) => {
	if (obj === undefined) return 'null';
	if (typeof obj !== 'object' || obj === null) return JSON.stringify(obj);
	if (Array.isArray(obj)) return `[${obj.map(stableStringify).join(',')}]`;
	const keys = Object.keys(obj).sort();
	return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
};
